import React, { useEffect, useState, useImperativeHandle, forwardRef, useRef } from "react";
import { useDispatch } from "react-redux";
import { updateFile } from "../../features/user/userSlice";
import { useTranslation } from "react-i18next";
import { SXEditField } from "@common/components/ui-components/SXEditField";
import { Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";

interface KeywordPillsProps {
  sfile: any;
  selectedProjectName: string;
  selectedFile: number;
  checkedCount: number;
  onDirtyChange?: (dirty: boolean) => void;
}

export interface KeywordPillsHandle {
  /** Commit any text currently in the input field as tags. */
  commitPending: () => void;
}

const KeywordPills = forwardRef<KeywordPillsHandle, KeywordPillsProps>(
  ({ sfile, selectedProjectName, selectedFile, checkedCount, onDirtyChange }, ref) => {
    const dispatch = useDispatch();
    const [inputValue, setInputValue] = useState("");
    const [isEditorVisible, setIsEditorVisible] = useState(false);
    const inputContainerRef = useRef<HTMLDivElement>(null);
    const { t } = useTranslation();

    // Separate visible user keywords from hidden system keywords (JSON objects like {status:...})
    const allKeywords: string[] = Array.isArray(sfile.keywords) ? sfile.keywords : [];
    const hiddenKeywords = allKeywords.filter((k: string) => typeof k === "string" && k.startsWith("{"));
    const keywords = allKeywords.filter((k: string) => typeof k === "string" && !k.startsWith("{") && k.trim() !== "");

    const updateKeywords = (newVisibleKeywords: string[]) => {
      // Preserve hidden system keywords alongside user-visible ones
      const combined = [...hiddenKeywords, ...newVisibleKeywords];
      dispatch(
        updateFile({
          file: sfile,
          field: "keywords",
          value: combined,
          selectedProject: selectedProjectName,
          index: selectedFile,
        }),
      );
    };

    const commitInput = (raw: string) => {
      const newValues = raw
        .split(",")
        .map((val) => val.trim())
        .filter((val) => val.length > 0 && !keywords.includes(val));

      if (newValues.length > 0) {
        updateKeywords([...keywords, ...newValues]);
      }
      setInputValue("");
      setIsEditorVisible(false);
      onDirtyChange?.(false);
    };

    const closeEditor = () => {
      if (inputValue.trim()) {
        commitInput(inputValue);
        return;
      }

      setInputValue("");
      setIsEditorVisible(false);
      onDirtyChange?.(false);
    };

    useEffect(() => {
      setInputValue("");
      setIsEditorVisible(false);
      onDirtyChange?.(false);
    }, [selectedProjectName, selectedFile, onDirtyChange]);

    useEffect(() => {
      if (!isEditorVisible) {
        return;
      }

      const frameId = requestAnimationFrame(() => {
        const input = inputContainerRef.current?.querySelector("input, textarea") as
          | HTMLInputElement
          | HTMLTextAreaElement
          | null;

        input?.focus();
      });

      return () => {
        cancelAnimationFrame(frameId);
      };
    }, [isEditorVisible]);

    useImperativeHandle(ref, () => ({
      commitPending: () => {
        if (inputValue.trim()) {
          commitInput(inputValue);
        }
      },
    }));

    const handleInputChange = (val: string) => {
      // If user types a comma, process immediately
      if (val.includes(",")) {
        commitInput(val);
      } else {
        setInputValue(val);
        onDirtyChange?.(val.trim().length > 0);
      }
    };

    const handleRemoveKeyword = (keyword: string) => {
      const updated = keywords.filter((k: string) => k !== keyword);
      updateKeywords(updated);
    };

    return (
      <div className="d-flex flex-column gap-2 pt-2">
        {!isEditorVisible && (
          <div>
            <Button
              type="button"
              className="secondary-btn ms-0"
              disabled={checkedCount > 0}
              onClick={() => {
                setIsEditorVisible(true);
              }}
            >
              <FontAwesomeIcon icon={faPlus} className="me-1" />
              {t("add")} {t("tags").toLowerCase()}
            </Button>
          </div>
        )}

        {isEditorVisible && (
          <div
            ref={inputContainerRef}
            onBlurCapture={(event) => {
              const nextTarget = event.relatedTarget as Node | null;
              if (nextTarget && inputContainerRef.current?.contains(nextTarget)) {
                return;
              }

              closeEditor();
            }}
          >
            <SXEditField
              type="text"
              textAlign="left"
              fullWidth
              border
              id="keyword-input"
              placeholder={`${t("add")} ${t("tags").toLowerCase()}`}
              val={inputValue}
              disabled={checkedCount > 0}
              instantupdate
              onOldUpdateCallback={(newValue) => {
                handleInputChange(newValue);
              }}
              onUpdate={(newValue) => {
                setInputValue(String(newValue));
              }}
            />
          </div>
        )}

        {keywords.length > 0 && (
          <div className="pill-wrapper">
            {keywords.map((keyword: string) => (
              <div className="pill-container" key={keyword}>
                <span>{keyword}</span>
                <button
                  type="button"
                  className="pill-remove-button"
                  aria-label={`Remove tag: ${keyword}`}
                  onClick={() => handleRemoveKeyword(keyword)}
                  disabled={checkedCount > 0}
                >
                  &times;
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  },
);

export default KeywordPills;
